/**
 * Admin Console → Workspaces: every workspace on the platform (gods only).
 *
 * The table lists name, owner, member count, status and creation date; a row
 * opens the drill-down (`WorkspaceDetail`: members, roles, feature flags) and
 * the row menu enters the workspace or opens the drill-down.  The list is
 * filtered client-side: a search over the workspace name, company, owner and
 * every member's email or display name, and, on SaaS, an "enabled
 * features" multi-select that keeps only workspaces with every picked flag
 * (`WorkspaceFeatureFilter`).  "New workspace" creates one owned by the god
 * through `POST /workspaces` without switching the session into it.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Avatar,
    Button,
    DataTable,
    Input,
    Modal,
    PopoverMenu,
    Search,
    SparkIcon,
    SparkModalPortal,
    TableToolbarContent,
    TagColored,
    getOverlayPortalParent,
    sparkToast,
    useAnchoredPanelPosition,
    useEscapeKey,
    useOutsideClick,
} from '@spark';
import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import { useAuth } from '@/features/auth/AuthContext';
import { goHomeAfterWorkspaceChange } from '@/features/workspaces/utils/goHomeAfterWorkspaceChange';
import { sanitizeWorkspaceName } from '@/features/workspaces/utils/workspaceName';
import { formatLongDate } from '@/features/billing/utils/billingFormat';
import { createWorkspaceAsGod, fetchAllWorkspaces } from '../api/adminApi';
import {
    ENTER_WORKSPACE_LABEL,
    NEW_WORKSPACE_LABEL,
    VIEW_WORKSPACE_LABEL,
    WORKSPACE_NAME_REQUIRED,
    WORKSPACE_STATUS_TAG,
} from '../constants';
import WorkspaceDetail from './WorkspaceDetail';
import WorkspaceFeatureFilter, { hasEveryFeature } from './WorkspaceFeatureFilter';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const EMPTY_CELL = '-';
export const NO_MATCHING_WORKSPACES = 'No workspaces match your filters';
export const SEARCH_PLACEHOLDER = 'Search by name, owner or member';

/** Every member's email and display name (`members` is missing on a backend from before it was added). */
const memberSearchFields = (ws) =>
    (Array.isArray(ws.members) ? ws.members : []).flatMap((member) => [member?.email, member?.name]);

/** Row menu: enter the workspace (session switch + home) or open the drill-down. */
function WorkspaceRowActions({ onEnter, onView, entering }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const close = () => setOpen(false);

    useOutsideClick([wrapRef, panelRef], close, open);
    useEscapeKey(close, open);
    const panelStyle = useAnchoredPanelPosition(wrapRef, panelRef, open, 'end');

    const panel = open ? (
        <div ref={panelRef} className="settings-users__row-actions-panel" style={panelStyle}>
            <PopoverMenu
                tip={false}
                items={[
                    {
                        label: VIEW_WORKSPACE_LABEL,
                        prefixIconSlug: 'eye',
                        showPrefixIcon: true,
                        onClick: () => {
                            setOpen(false);
                            onView();
                        },
                    },
                    {
                        label: ENTER_WORKSPACE_LABEL,
                        prefixIconSlug: 'arrow-right',
                        showPrefixIcon: true,
                        state: entering ? 'disabled' : 'default',
                        onClick: () => {
                            setOpen(false);
                            onEnter();
                        },
                    },
                ]}
            />
        </div>
    ) : null;

    return (
        <div ref={wrapRef} className="settings-users__row-actions">
            <Button
                variant="ghost"
                size="small"
                aria-label="Workspace actions"
                icon={<SparkIcon slug="dots" size="small" />}
                onClick={(event) => {
                    event.stopPropagation();
                    setOpen((prev) => !prev);
                }}
            />
            {open ? createPortal(panel, getOverlayPortalParent(wrapRef.current)) : null}
        </div>
    );
}

function CreateWorkspaceDialog({ open, onClose, onCreated }) {
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName('');
        setError('');
        setSubmitting(false);
    }, [open]);

    const close = () => {
        if (submitting) return;
        onClose();
    };

    const submit = async () => {
        if (submitting) return;
        const trimmed = sanitizeWorkspaceName(name, { trimEnd: true }).trim();
        if (!trimmed) {
            setError(WORKSPACE_NAME_REQUIRED);
            return;
        }
        setError('');
        setSubmitting(true);
        const result = await createWorkspaceAsGod(trimmed);
        setSubmitting(false);
        if (!result.ok) {
            // A taken name (409) is the god's to fix, so it belongs next to the field.
            if (result.status === 409) setError(result.error);
            else sparkToast.error(result.error);
            return;
        }
        sparkToast.success(`Workspace "${trimmed}" was created`);
        onCreated(result.data?.workspace);
    };

    return (
        <SparkModalPortal open={open} onClose={close} width={480} destroyOnClose>
            <Modal
                open
                title={NEW_WORKSPACE_LABEL}
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                primaryLabel={submitting ? 'Creating…' : 'Create workspace'}
                primaryDisabled={submitting}
                tertiaryLabel="Cancel"
                tertiaryDisabled={submitting}
                onPrimaryClick={submit}
                onTertiaryClick={close}
                onCloseClick={close}
            >
                <form
                    className="admin-form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        submit();
                    }}
                >
                    <Input
                        label="Workspace name"
                        placeholder="Workspace name"
                        size="medium"
                        value={name}
                        danger={Boolean(error)}
                        helperText={error || undefined}
                        showHelperText={Boolean(error)}
                        disabled={submitting}
                        maxLength={100}
                        autoFocus
                        onChange={(event) => {
                            setName(sanitizeWorkspaceName(event.target.value));
                            if (error) setError('');
                        }}
                    />
                    <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
                </form>
            </Modal>
        </SparkModalPortal>
    );
}

export default function WorkspacesAdmin({ selectedWorkspaceId, onSelectWorkspace, featuresEditable }) {
    const { workspace: activeWorkspace, switchWorkspace } = useAuth();
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [query, setQuery] = useState('');
    // Feature ids every listed workspace must have enabled (SaaS only; empty = no feature filter).
    const [requiredFeatures, setRequiredFeatures] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [createOpen, setCreateOpen] = useState(false);
    const [entering, setEntering] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        const result = await fetchAllWorkspaces();
        if (!result.ok) {
            setWorkspaces([]);
            setLoadError(result.error);
        } else {
            setWorkspaces(Array.isArray(result.data?.workspaces) ? result.data.workspaces : []);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        setPage(1);
    }, [query, requiredFeatures, pageSize]);

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const matchesQuery = (ws) =>
            !needle ||
            [ws.name, ws.owner_email, ws.company, ...memberSearchFields(ws)].some((field) =>
                String(field || '').toLowerCase().includes(needle),
            );
        return workspaces.filter((ws) => matchesQuery(ws) && hasEveryFeature(ws, requiredFeatures));
    }, [workspaces, query, requiredFeatures]);
    const isFiltering = Boolean(query.trim()) || requiredFeatures.length > 0;

    const paged = useMemo(() => filtered.slice((page - 1) * pageSize, page * pageSize), [filtered, page, pageSize]);

    const enterWorkspace = async (ws) => {
        if (entering) return;
        setEntering(ws.id);
        try {
            await switchWorkspace(ws.id);
        } catch (error) {
            setEntering(null);
            sparkToast.error(error?.message || 'Failed to enter the workspace.');
            return;
        }
        goHomeAfterWorkspaceChange();
    };

    const handleWorkspaceChanged = useCallback((updated) => {
        setWorkspaces((prev) => prev.map((ws) => (ws.id === updated.id ? { ...ws, ...updated } : ws)));
    }, []);

    if (selectedWorkspaceId) {
        return (
            <WorkspaceDetail
                workspaceId={selectedWorkspaceId}
                featuresEditable={featuresEditable}
                isActive={activeWorkspace?.id === selectedWorkspaceId}
                entering={entering === selectedWorkspaceId}
                onBack={() => onSelectWorkspace(null)}
                onEnter={(ws) => enterWorkspace(ws)}
                onWorkspaceChanged={handleWorkspaceChanged}
            />
        );
    }

    const columns = [
        {
            title: 'Workspace',
            key: 'name',
            dataIndex: 'name',
            minWidth: 200,
            flex: 1,
            render: (_, ws) => (
                <div className="admin-workspace-cell">
                    <Avatar
                        size="medium"
                        type="initials"
                        alt={ws.name}
                        primaryText={ws.name}
                        secondaryText={ws.company || ''}
                        showContent
                    />
                    {ws.id === activeWorkspace?.id ? <TagColored label="Current" variant="blue" /> : null}
                    {ws.is_default ? <TagColored label="Migrated" variant="disabled" /> : null}
                </div>
            ),
        },
        {
            title: 'Owner',
            key: 'owner_email',
            dataIndex: 'owner_email',
            minWidth: 160,
            render: (value) => value || EMPTY_CELL,
        },
        {
            title: 'Members',
            key: 'member_count',
            dataIndex: 'member_count',
            width: 90,
            render: (value) => (Number.isFinite(Number(value)) ? Number(value) : EMPTY_CELL),
        },
        {
            title: 'Created',
            key: 'created_at',
            dataIndex: 'created_at',
            width: 120,
            render: (value) => formatLongDate(value) || EMPTY_CELL,
        },
        {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            width: 100,
            render: (value) => {
                const tag = WORKSPACE_STATUS_TAG[value] || WORKSPACE_STATUS_TAG.active;
                return <TagColored label={tag.label} variant={tag.variant} />;
            },
        },
        {
            title: '',
            key: 'actions',
            width: 56,
            render: (_, ws) => (
                <WorkspaceRowActions
                    entering={Boolean(entering)}
                    onView={() => onSelectWorkspace(ws.id)}
                    onEnter={() => enterWorkspace(ws)}
                />
            ),
        },
    ];

    const emptyText = loading ? 'Loading workspaces...' : loadError || (isFiltering ? NO_MATCHING_WORKSPACES : 'No workspaces yet');

    return (
        <>
            <SettingsSectionCard
                iconSlug="workspace"
                title="Workspaces"
                headerExtra={(
                    <>
                        <Button variant="tertiary" size="medium" disabled={loading} onClick={load}>
                            Refresh
                        </Button>
                        <Button variant="primary" size="medium" onClick={() => setCreateOpen(true)}>
                            {NEW_WORKSPACE_LABEL}
                        </Button>
                    </>
                )}
            >
                <div className="settings-users">
                    <div className="settings-users__filters">
                        <div className="settings-users__search">
                            <Search
                                size="medium"
                                placeholder={SEARCH_PLACEHOLDER}
                                aria-label="Search workspaces"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                onClear={() => setQuery('')}
                            />
                        </div>
                        {featuresEditable ? (
                            <WorkspaceFeatureFilter selected={requiredFeatures} onChange={setRequiredFeatures} />
                        ) : null}
                    </div>
                    <div className="settings-users__table-block">
                        <div className="settings-users__toolbar">
                            <TableToolbarContent
                                title={`Workspaces (${filtered.length})`}
                                showInfoIconTitle={false}
                                showSubtitle={false}
                            />
                        </div>
                        <DataTable
                            rowKey={(record) => String(record.id)}
                            columns={columns}
                            dataSource={paged}
                            onRowClick={(record) => onSelectWorkspace(record.id)}
                            pagination={{
                                current: page,
                                pageSize,
                                total: filtered.length,
                                pageSizeOptions: PAGE_SIZE_OPTIONS,
                                onChange: setPage,
                                onPageSizeChange: (size) => {
                                    setPageSize(size);
                                    setPage(1);
                                },
                            }}
                            emptyText={emptyText}
                        />
                    </div>
                </div>
            </SettingsSectionCard>

            <CreateWorkspaceDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => {
                    setCreateOpen(false);
                    load();
                }}
            />
        </>
    );
}
